// import hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// import components
import { Box } from '@mui/material';
import { DateNavigation } from 'components/shared';
import { Period } from 'components/shared/DateNavigation/DateNavigation.types';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

// import queries
import { queries } from 'RestAPIs/rapini';

// import types
import { DutyResponse } from 'generated/index';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';

// import utils
import dayjs from 'dayjs';
import {
  flattenToDate,
  formatDate,
  formatTime,
  getMonthInterval,
  getWeekInterval
} from 'utils/date';
import { isSDBAccount } from 'utils/misc';

import { FILTER } from './RegionalPoolRequestsGrid.enums';

interface Props {
  isRegionalPool: boolean;
  filter: FILTER;
  period: Period;
}

const RegionalPoolRequestsGrid = ({
  isRegionalPool,
  filter,
  period
}: Props) => {
  const { t } = useTranslation('common');
  const { startDate } =
    period == 'week'
      ? getWeekInterval(new Date())
      : getMonthInterval(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(startDate);
  const interval =
    period == 'week'
      ? getWeekInterval(selectedDate)
      : getMonthInterval(selectedDate);
  const { data: duties, isLoading } = queries.useGetDuties(
    dayjs(interval.startDate).format('YYYY-MM-DD'),
    dayjs(interval.endDate).format('YYYY-MM-DD'),
    undefined,
    false,
    true,
    filter === FILTER.OPEN || filter === FILTER.ALL,
    filter === FILTER.ALL ||
      filter === FILTER.REGIONALPOOL ||
      filter === FILTER.PENDING,
    filter === FILTER.ALL,
    false,
    true
  );

  let columns: GridColDef<GridValidRowModel>[] = [
    {
      field: 'requestId',
      headerName: t('requestNumber')
    },
    {
      field: 'organisation',
      headerName: t('organization'),
      minWidth: 150
    },
    {
      field: 'assignedOrganizationalUnit',
      headerName: t('planUnit'),
      minWidth: 150
    },
    {
      field: 'date',
      headerName: t('date'),
      minWidth: 80,
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'start',
      headerName: t('startTime'),
      minWidth: 70,
      renderCell: ({ value }) => formatTime(value)
    },
    {
      field: 'end',
      headerName: t('endTime'),
      minWidth: 70,
      renderCell: ({ value }) => formatTime(value)
    },
    {
      field: 'dutyCode',
      headerName: t('dutyCode'),
      minWidth: 80
    },
    {
      field: 'function',
      headerName: t('function'),
      minWidth: 150
    },
    {
      field: 'qualifications',
      headerName: t('qualifications')
    },
    {
      field: 'notes',
      headerName: t('notes'),
      minWidth: 40
    },
    {
      field: 'requestDate',
      headerName: t('requestDate'),
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'reach',
      headerName: t('reach'),
      minWidth: 40
    },
    {
      field: 'status',
      headerName: t('status'),
      minWidth: 80
    },
    {
      field: 'employee',
      headerName: t('employee'),
      minWidth: 200
    },
    {
      field: 'connectedOn',
      headerName: t('enteredDate'),
      renderCell: ({ value }) => formatDate(value)
    }
  ];

  if (!isRegionalPool) {
    columns = columns.filter((c) => c.field !== 'organisation');
  }

  if (!isSDBAccount()) {
    columns = columns.filter((c) => c.field !== 'reach');
  }

  if (filter === FILTER.OPEN) {
    columns = columns.filter(
      (c) => c.field !== 'employee' && c.field !== 'connectedOn'
    );
  }

  function getStatus(d) {
    if (d.status === 'OPEN') {
      if (d?.dutyOrganizationalUnitRequests?.[0].retractedOn) {
        return t(`dutyStatus.RETRACTED`);
      }
      if (dayjs(d?.start).isBefore(dayjs())) {
        return t(`dutyStatus.NOT_FILLED`);
      }
    }
    if (d.status !== 'OPEN' && !d?.assignedEmployee) {
      return t(`dutyStatus.RETRACTED`);
    }
    if (d.status === 'APPROVED') {
      return t(`dutyStatus.ASSIGNED`);
    }
    return t(`dutyStatus.${d.status}`);
  }

  function handleFilter(d: DutyResponse) {
    if (filter === FILTER.OPEN) {
      return (
        d.status === 'OPEN' &&
        !d?.dutyOrganizationalUnitRequests?.[0].retractedOn &&
        !dayjs(d?.start).isBefore(dayjs())
      );
    }
    if (filter === FILTER.REGIONALPOOL) {
      return d.status === 'APPROVED' && d?.assignedEmployee;
    }
    if (filter === FILTER.PENDING) {
      return d.status === 'PENDING' && d?.assignedEmployee;
    }
    return true;
  }

  const rows =
    duties
      ?.filter((d) => handleFilter(d))
      ?.filter((d) => !dayjs(d.start).isBefore(dayjs(interval.startDate)))
      ?.sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      )
      .map((d) => ({
        id: d.id,
        requestId: d.requestId,
        organisation: d.organisation?.name,
        assignedOrganizationalUnit: d.assignedOrganizationalUnit?.name,
        date: d.date,
        start: d.start,
        end: d.end,
        dutyCode: d.dutyCode?.code ?? d.dutyCode?.dutyCode ?? '',
        function: d.function?.description ?? '',
        qualifications:
          d.qualifications?.map(({ name }) => name).join(', ') ?? '',
        notes: d.notes && d.notes.length > 0 ? t('yes') : t('no'),
        requestDate: d.dutyOrganizationalUnitRequests?.[0].requestedOn,
        reach: d.reach ?? 0,
        status: getStatus(d),
        employee:
          d.status !== 'OPEN' && d?.assignedEmployee
            ? d?.assignedEmployee?.nameInformal
            : null,
        connectedOn:
          d.status !== 'OPEN' && d?.assignedEmployee ? d.connectedOn : undefined
      })) ?? [];

  return (
    <Box p={2}>
      <Box py={2}>
        <DateNavigation
          defaultPeriod={period}
          onDateChange={(date) => setSelectedDate(flattenToDate(date))}
        />
      </Box>
      <TableCRUDLayout columns={columns} rows={rows} loading={isLoading} />
    </Box>
  );
};

export default RegionalPoolRequestsGrid;
